import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Workspace {
  id: number;
  name: string;
  color: 'pink' | 'yellow' | 'purple' | 'mint' | 'blue';
  type: 'team' | 'personal';
  is_default: number;
  created_at: string;
  updated_at: string;
}

interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean; // Track if persist hydration is complete

  // Actions
  setWorkspaces: (workspaces: Workspace[]) => void;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  addWorkspace: (workspace: Workspace) => void;
  updateWorkspace: (id: number, updates: Partial<Workspace>) => void;
  removeWorkspace: (id: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHasHydrated: (state: boolean) => void;
  fetchWorkspaces: (token: string) => Promise<void>;
  createWorkspace: (token: string, data: { name: string; color: string; type: string }) => Promise<Workspace | null>;
  switchWorkspace: (token: string, workspaceId: number) => Promise<void>;
  deleteWorkspace: (token: string, workspaceId: number) => Promise<boolean>;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Color mapping for workspace avatars
export const workspaceColors: Record<string, string> = {
  pink: '#EC4899',
  yellow: '#F59E0B',
  purple: '#8B5CF6',
  mint: '#10B981',
  blue: '#3B82F6',
};

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      currentWorkspace: null,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      setWorkspaces: (workspaces) => set({ workspaces }),

      setCurrentWorkspace: (workspace) => set({ currentWorkspace: workspace }),

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      addWorkspace: (workspace) => {
        const { workspaces } = get();
        set({ workspaces: [...workspaces, workspace] });
      },

      updateWorkspace: (id, updates) => {
        const { workspaces, currentWorkspace } = get();
        const updated = workspaces.map((w) =>
          w.id === id ? { ...w, ...updates } : w
        );
        set({
          workspaces: updated,
          currentWorkspace:
            currentWorkspace?.id === id
              ? { ...currentWorkspace, ...updates }
              : currentWorkspace,
        });
      },

      removeWorkspace: (id) => {
        const { workspaces, currentWorkspace } = get();
        const filtered = workspaces.filter((w) => w.id !== id);
        set({
          workspaces: filtered,
          currentWorkspace:
            currentWorkspace?.id === id
              ? filtered.find((w) => w.is_default === 1) || filtered[0] || null
              : currentWorkspace,
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      fetchWorkspaces: async (token) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/workspaces`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await response.json() as { success: boolean; data?: Workspace[]; error?: string };

          if (data.success && data.data) {
            const workspaces = data.data;
            const { currentWorkspace } = get();

            // If no current workspace, set the default one
            let activeWorkspace = currentWorkspace;
            if (!activeWorkspace || !workspaces.find((w) => w.id === activeWorkspace?.id)) {
              activeWorkspace = workspaces.find((w) => w.is_default === 1) || workspaces[0] || null;
            }

            set({
              workspaces,
              currentWorkspace: activeWorkspace,
              isLoading: false,
            });
          } else {
            set({ error: data.error || 'Failed to fetch workspaces', isLoading: false });
          }
        } catch (error) {
          console.error('Failed to fetch workspaces:', error);
          set({ error: 'Failed to fetch workspaces', isLoading: false });
        }
      },

      createWorkspace: async (token, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/workspaces`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          const result = await response.json() as { success: boolean; data?: Workspace; error?: string };

          if (result.success && result.data) {
            const workspace = result.data;
            get().addWorkspace(workspace);
            set({ isLoading: false });
            return workspace;
          } else {
            set({ error: result.error || 'Failed to create workspace', isLoading: false });
            return null;
          }
        } catch (error) {
          console.error('Failed to create workspace:', error);
          set({ error: 'Failed to create workspace', isLoading: false });
          return null;
        }
      },

      switchWorkspace: async (_token, workspaceId) => {
        const { workspaces } = get();
        const workspace = workspaces.find((w) => w.id === workspaceId);
        if (workspace) {
          set({ currentWorkspace: workspace });
          // Note: We no longer set the workspace as default on switch
          // The default workspace is set once during creation and should remain fixed
          // This ensures proper data isolation between workspaces
        }
      },

      deleteWorkspace: async (token, workspaceId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/workspaces/${workspaceId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          const result = await response.json() as { success: boolean; error?: string };

          if (result.success) {
            get().removeWorkspace(workspaceId);
            set({ isLoading: false });
            return true;
          } else {
            set({ error: result.error || 'Failed to delete workspace', isLoading: false });
            return false;
          }
        } catch (error) {
          console.error('Failed to delete workspace:', error);
          set({ error: 'Failed to delete workspace', isLoading: false });
          return false;
        }
      },
    }),
    {
      name: 'sweetpilot-workspace',
      partialize: (state) => ({
        currentWorkspace: state.currentWorkspace,
      }),
      onRehydrateStorage: () => (state) => {
        // Called when hydration is complete
        state?.setHasHydrated(true);
      },
    }
  )
);
